import React from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import Image from '@atlaskit/image';
import { Anchor, Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import darkImage from './assets/discovery-tab-empty-state-dark.png';
import lightImage from './assets/discovery-tab-empty-state-light.png';
import { messages } from './messages';

const MANAGE_ROVO_CONNECTORS_URL =
	'https://support.atlassian.com/organization-administration/docs/manage-rovo-connectors';

const styles = cssMap({
	container: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		paddingTop: token('space.800'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.500'),
		paddingLeft: token('space.200'),
		gap: token('space.400'),
	},
	textContainer: {
		display: 'flex',
		maxWidth: '480px',
		width: '100%',
		flexDirection: 'column',
		alignItems: 'center',
		gap: token('space.050'),
		textAlign: 'center',
	},
	headingText: {
		alignSelf: 'stretch',
	},
	bodyText: {
		alignSelf: 'stretch',
	},
	image: {
		maxWidth: '480px',
		width: '100%',
	},
});

/**
 * Empty state shown in the discovery tab when there are no available third-party apps.
 *
 * Displays an illustration (light/dark aware), a heading, description text, and a link
 * prompting the user to ask their org admin to connect apps.
 */
export const DiscoveryContentEmptyState = (): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={styles.container} testId="discovery-content-empty-state">
			<Box xcss={styles.textContainer}>
				<Box xcss={styles.headingText}>
					<Heading size="medium">{formatMessage(messages.heading)}</Heading>
				</Box>
				<Box xcss={styles.bodyText}>
					<Text color="color.text.subtle">
						{formatMessage(messages.description)}{' '}
						<Anchor href={MANAGE_ROVO_CONNECTORS_URL} target="_blank" rel="noopener noreferrer">
							{formatMessage(messages.adminLink)}
						</Anchor>
					</Text>
				</Box>
			</Box>
			<Box xcss={styles.image}>
				<Image
					src={lightImage}
					srcDark={darkImage}
					alt=""
					testId="discovery-content-empty-state-image"
				/>
			</Box>
		</Box>
	);
};
